// src/screens/dashboard/RepairListPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Search, PlusCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as Fix } from "../../assets/images/fix.svg";

import PageHeader from "../../components/layout/PageHeader";
import DataTable from "../../components/table/Datatable";
import PaginationFooter from "../../components/table/PaginationFooter";
import DateRangeButton from "../../components/inputs/DateRangeButton";
import BatteryPill from "../../components/battery/battery";

import { createHttpClient } from "../../api/client";

function compareText(a, b) {
  const A = String(a ?? "").toLowerCase();
  const B = String(b ?? "").toLowerCase();
  if (A < B) return -1;
  if (A > B) return 1;
  return 0;
}

function buildCreatedAtDateParams(range) {
  const [start, end] = Array.isArray(range) ? range : [null, null];
  if (!start || !end) return {};

  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return {};

  s.setHours(0, 0, 0, 0);
  e.setHours(23, 59, 59, 999);

  return {
    "filters[createdAt][$gte]": s.toISOString(),
    "filters[createdAt][$lte]": e.toISOString(),
  };
}

function compareNumber(a, b) {
  const A = Number(a ?? 0);
  const B = Number(b ?? 0);
  if (A < B) return -1;
  if (A > B) return 1;
  return 0;
}

function ManageButton({ onClick, disabled = false }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`h-9 rounded-xl border-2 border-slate-200 bg-white px-4 text-sm font-bold text-[#047DED] hover:shadow-sm active:scale-[0.98] ${
        disabled ? "cursor-not-allowed opacity-50 text-gray-500 hover:shadow-none active:scale-100" : ""
      }`}
    >
      จัดการ
    </button>
  );
}

function RepairStatusPill({ value }) {
  const v = String(value ?? "").trim();

  const toneMap = {
    pending: "bg-[#FF95021A] text-[#FF9502]",
    in_progress: "bg-[#047DED1A] text-[#047DED]",
    completed: "bg-[#30CA171A] text-[#30CA17]",
    picked_up: "bg-[#1118271A] text-[#111827]",
  };

  const label =
    v === "pending"
      ? "รอดำเนินการ"
      : v === "in_progress"
        ? "กำลังซ่อม"
        : v === "completed"
          ? "ซ่อมเสร็จ"
          : v === "picked_up"
            ? "รับเครื่องแล้ว"
            : v
              ? v
              : "รออัพเดท";

  const tone = toneMap[v] || "bg-slate-100 text-slate-700";

  return (
    <span className={`inline-flex min-w-[96px] justify-center rounded-xl px-3 py-1 text-[12px] font-bold ${tone}`}> 
      {label}
    </span>
  );
}

function extractArray(resData) {
  if (Array.isArray(resData?.data)) return resData.data;

  if (Array.isArray(resData)) return resData;

  if (resData?.data && typeof resData.data === "object") return [resData.data];

  return [];
}

function extractRelationName(rel, fallback = "") {
  if (!rel) return fallback;
  if (typeof rel === "string") return rel;
  if (typeof rel === "object") {
    if (typeof rel.name === "string" && rel.name.trim()) return rel.name;
    if (typeof rel.size === "string" && rel.size.trim()) return rel.size;

    const nested = rel.data;
    if (nested && typeof nested === "object") {
      if (typeof nested.name === "string" && nested.name.trim()) return nested.name;
      if (typeof nested.size === "string" && nested.size.trim()) return nested.size;

      const attrs = nested.attributes;
      if (attrs && typeof attrs === "object") {
        if (typeof attrs.name === "string" && attrs.name.trim()) return attrs.name;
        if (typeof attrs.size === "string" && attrs.size.trim()) return attrs.size;
      }
    }
  }
  return fallback;
}

function mapRepairEntityToRow(entity) {
  const id = entity?.id ?? entity?.documentId ?? "";
  const productName = String(entity?.product_name || "").trim();
  const modelName = extractRelationName(entity?.model, "");
  const categoryName = extractRelationName(entity?.category, "");
  const deviceName = entity?.device_name ?? "";
  const itemName =
    productName ||
    (categoryName && modelName
      ? `${categoryName} ${modelName}`
      : categoryName || modelName || deviceName) ||
    "-";
  const capacityText = extractRelationName(entity?.capacity, String(entity?.capacity ?? "-")) || "-";
  const colorText = extractRelationName(entity?.color, String(entity?.color ?? "-")) || "-";

  return {
    id: String(id),

    item: itemName,
    cap: capacityText,
    color: colorText,

    battery:
      entity?.battery_health != null && entity?.battery_health !== ""
        ? Number(entity.battery_health)
        : null,

    gear: entity?.equipment?.name ?? entity?.equipment ?? "-",

    estimate:
      entity?.cost_estimate != null && entity?.cost_estimate !== ""
        ? Number(entity.cost_estimate)
        : 0,

    repair_status: entity?.repair_status ?? "-",

    quality: entity?.symptoms ?? "-",
    warranty: entity?.warranty_status ?? "-",
  };
}

export default function RepairListPage() {
  const navigate = useNavigate();
  const http = useMemo(() => createHttpClient(), []);

  // data state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // filters
  const [query, setQuery] = useState("");
  const [range, setRange] = useState([null, null]);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // sort state
  const [sortKey, setSortKey] = useState(""); // "item" | "estimate"
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"

  const onSort = (key) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return key;
    });
  };

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();

    (async () => {
      setLoading(true);
      setLoadError("");
      try {
        const params = {
          ...buildCreatedAtDateParams(range),
        };
        const res = await http.get("/repairs", { params, signal: ac.signal });
        const arr = extractArray(res?.data);
        const mapped = arr.map(mapRepairEntityToRow);

        if (!alive) return;
        setRows(mapped);
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        console.error("GET /repairs error:", err);
        if (!alive) return;
        const msg =
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "โหลดข้อมูลไม่สำเร็จ";
        setLoadError(msg);
        setRows([]);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [http, range?.[0], range?.[1]]);

  const filtered = useMemo(() => {
    let next = [...rows];
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      next = next.filter((r) =>
        [r.item, r.cap, r.color, r.gear, r.quality, r.warranty, r.estimate]
          .map((x) => String(x ?? "").toLowerCase())
          .some((x) => x.includes(q))
      );
    }
    return next;
  }, [rows, query]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;

    const next = [...filtered].sort((a, b) => {
      if (sortKey === "estimate") {
        const c = compareNumber(a?.estimate, b?.estimate);
        return sortDir === "asc" ? c : -c;
      }
      const c = compareText(a?.[sortKey], b?.[sortKey]);
      return sortDir === "asc" ? c : -c;
    });

    return next;
  }, [filtered, sortKey, sortDir]);

  const totalItems = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.max(1, Math.min(page, totalPages));

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safePage]);

  const paged = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, safePage, pageSize]);

  const columns = useMemo(
    () => [
      {
        key: "item",
        title: "รายการ",
        className: "font-semibold",
        sortable: true,
        headerClassName: "w-[250px]",
        sortKey: "item",
      },
      { key: "cap", title: "ความจุ" },
      { key: "color", title: "สี" },
      {
        key: "battery",
        title: "แบตเตอรี่",
        render: (r) => (r.battery != null ? <BatteryPill value={r.battery} /> : "-"),
      },
      { key: "gear", title: "อุปกรณ์" },
      {
        key: "estimate",
        title: "ราคาประเมิน",
        sortable: true,
        sortKey: "estimate",
        render: (r) => `฿${Number(r.estimate || 0).toLocaleString("th-TH")}`,
      },
      { key: "quality", title: "คุณภาพสินค้า" },
      { key: "warranty", title: "ประกันศูนย์" },
      {
        key: "repair_status",
        title: "สถานะ",
        render: (r) => <RepairStatusPill value={r.repair_status} />,
      },
      {
        key: "manage",
        title: "จัดการ",
        render: (r) => (
          <ManageButton onClick={() => navigate(`/app/repair/${r.id}`)} />
        ),
      },
    ],
    [navigate]
  );

  return (
    <div className="space-y-4 p-4 bg-white min-h-full">
      <PageHeader
        icon={Fix}
        breadcrumb="การซ่อม"
        title="รายการซ่อม"
        showExport={false}
        extraActions={
          <button
            type="button"
            onClick={() => navigate("/app/repair/create")}
            className="inline-flex items-center gap-2 rounded-xl bg-[#111111] px-4 py-3 text-sm font-bold text-white shadow-md hover:opacity-95 active:scale-[0.99]"
          >
            <PlusCircle size={18} />
            เพิ่มรายการซ่อม
          </button>
        }
      />

      {/* search bar */}
      <div className="rounded-2xl bg-[#F5F5F5] p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 rounded-xl border hover:border-2 border-[#1D161633] bg-white px-4 py-3 w-full md:w-2/5">
              <Search size={18} className="text-[#1D1616B2]" />
              <input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full bg-transparent text-sm outline-none placeholder:text-[#1D161680]"
                placeholder="ค้นหารายการ..."
              />
            </div>

            {loading && <div className="mt-2 text-sm text-slate-500">กำลังโหลดข้อมูล...</div>}
            {!loading && loadError && (
              <div className="mt-2 text-sm text-rose-600">โหลดข้อมูลไม่สำเร็จ: {loadError}</div>
            )}
          </div>

          <div className="flex w-full justify-start md:w-auto md:justify-end">
            <DateRangeButton mode="single" range={range} setRange={setRange} />
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={paged}
        rowHeight="h-16"
        headerBg="bg-slate-50"
        striped={false}
        tableMinWidth="min-w-[1100px]"
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={onSort}
      />

      <PaginationFooter
        totalItems={totalItems}
        page={safePage}
        pageSize={pageSize}
        onPageChange={(p) => setPage(p)}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
        pageSizeOptions={[50, 100, 200]}
      />
    </div>
  );
}
